import React, { createContext, useContext, useCallback, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { useGetJobProgressQuery } from '@/services/store/store';
import { JobProgressResponseDto, JobStatus } from '@/@types/job-progress';
import { apiSlice } from '@/services/http';
import { Tags } from '@/enum/tags';
import { isProduction } from '@/utils/environment';

const POLLING_INTERVAL = isProduction() ? 60_000 : 5_000;

// Armazenar o último jobId invalidado FORA do componente para persistir entre re-mounts
// Isso evita o loop infinito quando invalidateTags causa re-render da página
let lastInvalidatedJobId: string | null = null;

// Chave para armazenar jobs fechados manualmente no localStorage
const HIDDEN_JOBS_KEY = 'hidden_job_progress_ids';

interface JobProgressContextType {
  progress: JobProgressResponseDto | undefined;
  isLoading: boolean;
  isVisible: boolean;
  error: unknown;
  startPolling: () => void;
  hideProgress: () => void;
  refetch: () => void;
}

const JobProgressContext = createContext<JobProgressContextType | null>(null);

// Helper functions para localStorage
const getHiddenJobIds = (): Set<string> => {
  try {
    const stored = localStorage.getItem(HIDDEN_JOBS_KEY);
    return stored ? new Set(JSON.parse(stored)) : new Set();
  } catch {
    return new Set();
  }
};

const addHiddenJobId = (jobId: string): void => {
  try {
    const hidden = getHiddenJobIds();
    hidden.add(jobId);
    // Manter apenas os últimos 10 jobs para não acumular lixo
    const arr = Array.from(hidden).slice(-10);
    localStorage.setItem(HIDDEN_JOBS_KEY, JSON.stringify(arr));
  } catch {
    // Ignora erros de localStorage
  }
};

const isJobHidden = (jobId: string | undefined): boolean => {
  if (!jobId) return false;
  return getHiddenJobIds().has(jobId);
};

export const JobProgressProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const dispatch = useDispatch();
  const [manuallyHidden, setManuallyHidden] = React.useState(false);
  const [forcePolling, setForcePolling] = React.useState(false);
  const initialFetchDone = React.useRef(false);
  const pollingIntervalRef = React.useRef<NodeJS.Timeout | null>(null);
  const sawRunningRef = React.useRef(false);

  // Query sem polling nativo - vamos controlar manualmente
  const { data: progress, error, refetch, isLoading, isFetching } = useGetJobProgressQuery(undefined);

  // Guardar refetch em ref para não causar re-runs do useEffect
  const refetchRef = React.useRef(refetch);
  refetchRef.current = refetch;

  console.log('[JOB_PROGRESS] State:', {
    status: progress?.status,
    forcePolling,
    hasInterval: !!pollingIntervalRef.current,
    initialFetchDone: initialFetchDone.current,
    jobId: progress?.jobId
  });

  // Fetch inicial ao montar o componente
  React.useEffect(() => {
    console.log('[JOB_PROGRESS] Initial fetch effect - initialFetchDone:', initialFetchDone.current);
    if (!initialFetchDone.current) {
      initialFetchDone.current = true;
      console.log('[JOB_PROGRESS] Executing initial refetch...');
      refetchRef.current();
    }
  }, []);

  // Controla o polling manualmente
  React.useEffect(() => {
    const isRunning = progress?.status === JobStatus.RUNNING;
    const shouldPoll = isRunning || forcePolling;

    console.log('[JOB_PROGRESS] Polling effect:', {
      shouldPoll,
      status: progress?.status,
      forcePolling,
      sawRunning: sawRunningRef.current,
      currentInterval: !!pollingIntervalRef.current
    });

    // Marca que vimos o job rodando
    if (isRunning) {
      sawRunningRef.current = true;
    }

    // Desativa forcePolling SOMENTE quando:
    // 1. forcePolling está ativo
    // 2. Já vimos o job em RUNNING antes (sawRunningRef.current)
    // 3. O job não está mais RUNNING (terminou)
    if (forcePolling && sawRunningRef.current && !isRunning) {
      console.log('[JOB_PROGRESS] Disabling forcePolling - job finished after running');
      setForcePolling(false);
      sawRunningRef.current = false;
    }

    // Só manipula o intervalo se o estado de shouldPoll mudou
    if (shouldPoll && !pollingIntervalRef.current) {
      console.log('[JOB_PROGRESS] Starting new polling interval with', POLLING_INTERVAL, 'ms');
      pollingIntervalRef.current = setInterval(() => {
        console.log('[JOB_PROGRESS] Polling tick - calling refetch');
        refetchRef.current();
      }, POLLING_INTERVAL);
    } else if (!shouldPoll && pollingIntervalRef.current) {
      console.log('[JOB_PROGRESS] Stopping polling interval');
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
    }

    // Cleanup ao desmontar
    return () => {
      if (pollingIntervalRef.current) {
        console.log('[JOB_PROGRESS] Cleanup - clearing interval');
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }
    };
  }, [progress?.status, forcePolling]);

  // Determina visibilidade baseado no status do job
  const isVisible = useMemo(() => {
    if (manuallyHidden) return false;
    if (!progress) return false;

    // Verifica se este job específico foi fechado manualmente (persistido no localStorage)
    if (isJobHidden(progress.jobId)) return false;

    // Mostrar se está rodando, completou ou falhou
    return progress.status === JobStatus.RUNNING ||
           progress.status === JobStatus.COMPLETED ||
           progress.status === JobStatus.FAILED;
  }, [progress, manuallyHidden]);

  // Quando job completa ou falha, invalida cache das lojas (uma vez por jobId)
  React.useEffect(() => {
    const jobId = progress?.jobId;
    const isFinished = progress?.status === JobStatus.COMPLETED || progress?.status === JobStatus.FAILED;

    // Só invalida se: tem jobId, job terminou, e ainda não invalidamos esse jobId
    if (jobId && isFinished && lastInvalidatedJobId !== jobId) {
      lastInvalidatedJobId = jobId;
      dispatch(apiSlice.util.invalidateTags([Tags.STORE, Tags.STORE_CONCILIATION]));
    }

    // Reset quando um NOVO job começa (jobId diferente e status RUNNING)
    if (progress?.status === JobStatus.RUNNING && jobId && lastInvalidatedJobId !== null && lastInvalidatedJobId !== jobId) {
      lastInvalidatedJobId = null;
      setManuallyHidden(false);
    }
  }, [progress?.status, progress?.jobId, dispatch]);

  const startPolling = useCallback(() => {
    console.log('[JOB_PROGRESS] startPolling() called');
    setManuallyHidden(false);
    // Força o polling imediatamente (útil após iniciar um job)
    setForcePolling(true);
    console.log('[JOB_PROGRESS] setForcePolling(true) executed');
    // Não resetar lastInvalidatedJobId aqui - ele é controlado pelo jobId
    refetch();
    console.log('[JOB_PROGRESS] refetch() executed from startPolling');
  }, [refetch]);

  const hideProgress = useCallback(() => {
    setManuallyHidden(true);
    // Persiste no localStorage para que o job fechado não reapareça após refresh
    if (progress?.jobId) {
      addHiddenJobId(progress.jobId);
    }
  }, [progress?.jobId]);

  const contextValue = useMemo(() => ({
    progress,
    isLoading: isLoading || isFetching,
    isVisible,
    error,
    startPolling,
    hideProgress,
    refetch,
  }), [progress, isLoading, isFetching, isVisible, error, startPolling, hideProgress, refetch]);

  return (
    <JobProgressContext.Provider value={contextValue}>
      {children}
    </JobProgressContext.Provider>
  );
};

export const useJobProgress = (): JobProgressContextType => {
  const context = useContext(JobProgressContext);
  if (!context) {
    throw new Error('useJobProgress must be used within JobProgressProvider');
  }
  return context;
};
